/**
 * V2PaywallSubscriptionsSection — V2-native editor section for subscription products
 *
 * Edits: Product catalog (viewKind, productLayout, items)
 * V2 paths: content.paywall.products.*
 *
 * Replaces: OnboardingSubscriptionsSection (for V2 context)
 * Reuses: V2-owned SubscriptionItemEditor, SubscriptionTypeSelector, SubscriptionStyleSections, SortableList
 *
 * Key V2 differences:
 * - subscriptionViewKind → viewKind (CatalogViewKind)
 * - itemType → productLayout (ProductLayout)
 * - subscriptionId → productId
 * - leftLabelTop → topStartLabel, leftLabelBottom → bottomStartLabel
 * - rightLabelTop → topEndLabel, rightLabelBottom → bottomEndLabel
 * - isDefault → isSelected
 * - l10n → translations, styles → appearance
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Collapse } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SAccordion, SortableList } from '@modules/ui-components';
import { usePaywallProducts } from '@modules/paywall-products';
// Import directly from the hook file (NOT the barrel) — the @modules/stripe-connect
// barrel transitively loads StripeIntegrationContent.tsx which uses `import.meta`,
// incompatible with the test runner's CommonJS module mode.
import { usePaddlePickerConnection } from '@modules/reactFlow/components/onboarding/editorSectionsV2/paywall/usePaddlePickerConnection';
import { useStripeConnect } from '@modules/stripe-connect/hooks/useStripeConnect';
import type { PaywallProduct } from '@segmently/schema-models/payments';
import { CatalogViewKind } from '@segmently/schema-models/v2';
import { EditorGuideProvider } from '../../../contexts';
import {
  V2_SUBSCRIPTION_CELL_TYPE_PATH,
  V2_SUBSCRIPTION_ITEM_TYPE_PATH,
  V2_SUBSCRIPTION_CELL_TYPE_OPTIONS,
  V2_SUBSCRIPTION_ITEM_TYPE_OPTIONS,
  V2_SUBSCRIPTION_STYLE_SECTIONS,
  createDefaultV2ProductOffer,
  type SubscriptionCellType,
  type SubscriptionItemType,
} from '../../../config/v2PaywallSubscriptionsConfig';
import { SECTION_MARGIN_BOTTOM } from '../../../config/ui';
import { getValueByPath } from '../../../utils/v2FieldOperations';
import { getSectionTestId } from '../../../utils/testIdUtils';
import { SubscriptionTypeSelector } from './paywall/SubscriptionTypeSelector';
import { SubscriptionStyleSections } from './paywall/SubscriptionStyleSections';
import { SubscriptionItemEditor, type SubscriptionItemData } from './paywall/SubscriptionItemEditor';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

export interface V2PaywallSubscriptionsSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;
  /** Stable scope used to preserve nested picker UI state across parent remounts. */
  pickerPersistenceScope?: string;
  /**
   * M7: optional save handler from the parent V2 screen editor dialog.
   * When provided, the picker shows a save-guard modal before triggering
   * the Stripe OAuth redirect. Threaded down to SubscriptionItemEditor →
   * PaywallProductPickerControl.
   */
  onSaveScreen?: () => Promise<void>;
  /**
   * M7: whether the parent editor has unsaved changes. Drives the
   * save-guard modal's button set in the picker. Ignored when
   * `onSaveScreen` is not provided.
   */
  hasUnsavedChanges?: boolean;
}

/**
 * V2 ProductOffer structure (subset for editor mapping)
 */
interface V2ProductOffer {
  productId: string;
  topStartLabel?: { translations: Record<string, string> };
  bottomStartLabel?: { translations: Record<string, string> };
  topEndLabel?: { translations: Record<string, string> };
  bottomEndLabel?: { translations: Record<string, string> };
  descriptionLabel?: { translations: Record<string, string> };
  purchaseLabel?: { translations: Record<string, string> };
  badge?: { translations: Record<string, string>; kind?: string; appearance?: Record<string, unknown> } | null;
  isSelected: boolean;
  [key: string]: unknown;
}

function getV2LabelValue(labelObj: unknown, locale: string): string {
  if (!labelObj || typeof labelObj !== 'object') return '';
  const label = labelObj as Record<string, unknown>;
  const translations = label.translations as Record<string, string> | undefined;
  if (!translations) return '';
  return translations[locale] || '';
}

/**
 * Transform V2 ProductOffer items to SubscriptionItemData for the V2-owned item editor.
 * Maps V2 field names to the editor's flat prop names.
 */
function transformToEditableItems(
  items: unknown[],
  locale: string
): (SubscriptionItemData & { id: string; originalIndex: number })[] {
  return items.map((item, index) => {
    const offer = item as V2ProductOffer;
    return {
      id: `subscription-${index}`,
      originalIndex: index,
      productId: offer.productId || '',
      leftLabelTop: getV2LabelValue(offer.topStartLabel, locale),
      leftLabelBottom: getV2LabelValue(offer.bottomStartLabel, locale),
      rightLabelTop: getV2LabelValue(offer.topEndLabel, locale),
      rightLabelBottom: getV2LabelValue(offer.bottomEndLabel, locale),
      descriptionLabel: getV2LabelValue(offer.descriptionLabel, locale),
      purchaseLabel: getV2LabelValue(offer.purchaseLabel, locale),
      badge: offer.badge ? getV2LabelValue(offer.badge, locale) : null,
      isDefault: Boolean(offer.isSelected),
    };
  });
}

/**
 * Map flat SubscriptionItemData field names back to V2 ProductOffer paths.
 */
const V2_FIELD_MAP: Record<string, string> = {
  topStartLabel: 'topStartLabel',
  bottomStartLabel: 'bottomStartLabel',
  topEndLabel: 'topEndLabel',
  bottomEndLabel: 'bottomEndLabel',
  descriptionLabel: 'descriptionLabel',
  purchaseLabel: 'purchaseLabel',
  // These match legacy-compatible field names kept by the editor UI:
  leftLabelTop: 'topStartLabel',
  leftLabelBottom: 'bottomStartLabel',
  rightLabelTop: 'topEndLabel',
  rightLabelBottom: 'bottomEndLabel',
};

const ITEMS_PATH = 'content.paywall.products.items';

export const V2PaywallSubscriptionsSection: React.FC<V2PaywallSubscriptionsSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  sectionTitle,
  pickerPersistenceScope,
  onSaveScreen,
  hasUnsavedChanges = false,
}) => {
  const [stylesExpanded, setStylesExpanded] = useState(false);
  const paddleConnection = usePaddlePickerConnection(projectId || '');

  const {
    products: paywallProducts,
    loading: productsLoading,
    error: productsError,
    refresh: refreshPaywallProducts,
    createProduct: createPaywallProduct,
  } = usePaywallProducts({
    projectId: projectId || '',
    autoLoad: Boolean(projectId),
  });


  /**
   * Stripe Connect status — lifted to section scope so a single subscription
   * covers all plan rows. Picker rows receive the derived booleans/strings as
   * props instead of each calling `useStripeConnect` themselves (which would
   * fan out N concurrent test+live status fetches).
   *
   * Live tab gating uses the verified `connected` field on
   * `StripeAccountStatusResponse` plus a `chargesEnabled !== false` guard.
   * `chargesEnabled === false` means the live account has been disabled for
   * charges (compliance hold, manual pause) — UX must keep the tab off until
   * the underlying account is healthy.
   *
   * Fail-closed: while loading or on error, the tab stays disabled with a
   * descriptive tooltip explaining why.
   */
  const {
    testStatus,
    liveStatus,
    loading: stripeLoading,
    error: stripeError,
  } = useStripeConnect(projectId || '');

  // ── Sandbox tab connection state (M7) ──
  // Mirror of the live-tab derivation. Sandbox almost always connects in
  // a test project, but a fresh project starts disconnected — the picker
  // then shows a Connect CTA banner instead of an empty product list.
  const sandboxConnected = useMemo(() => {
    if (stripeLoading || stripeError) return false;
    if (testStatus?.connected !== true) return false;
    if (testStatus?.chargesEnabled === false) return false;
    return true;
  }, [stripeLoading, stripeError, testStatus]);

  const sandboxNotConnectedReason = useMemo<string | null>(() => {
    if (stripeLoading) return 'Checking Stripe connection…';
    if (stripeError) {
      return 'Cannot verify Sandbox account connection — check Settings → Integrations';
    }
    if (testStatus?.connected !== true) {
      return 'Connect a Sandbox Stripe account first in Settings → Integrations';
    }
    if (testStatus?.chargesEnabled === false) {
      return 'Sandbox account is not yet enabled for charges';
    }
    return null;
  }, [stripeLoading, stripeError, testStatus]);

  // ── Live tab connection state ──
  // (M7 renames: previously `liveTabEnabled`/`liveTabDisabledReason`.
  // Now `liveConnected`/`liveNotConnectedReason` to reflect that the tab
  // itself is NEVER disabled — only its content swaps to a Connect CTA.)
  const liveConnected = useMemo(() => {
    if (stripeLoading || stripeError) return false;
    if (liveStatus?.connected !== true) return false;
    if (liveStatus?.chargesEnabled === false) return false;
    return true;
  }, [stripeLoading, stripeError, liveStatus]);

  const liveNotConnectedReason = useMemo<string | null>(() => {
    if (stripeLoading) return 'Checking Stripe connection…';
    if (stripeError) {
      return 'Cannot verify Live account connection — check Settings → Integrations';
    }
    if (liveStatus?.connected !== true) {
      return 'Connect a Live Stripe account first in Settings → Integrations';
    }
    if (liveStatus?.chargesEnabled === false) {
      return 'Live account is not yet enabled for charges';
    }
    return null;
  }, [stripeLoading, stripeError, liveStatus]);

  // V2 uses viewKind (was subscriptionViewKind)
  const cellType = useMemo((): SubscriptionCellType => {
    const value = getValueByPath<string>(step, V2_SUBSCRIPTION_CELL_TYPE_PATH);
    return (value as SubscriptionCellType) || CatalogViewKind.Vertical;
  }, [step]);

  // V2 uses productLayout (was itemType)
  const itemType = useMemo((): SubscriptionItemType => {
    const value = getValueByPath<string>(step, V2_SUBSCRIPTION_ITEM_TYPE_PATH);
    return (value as SubscriptionItemType) || 'CheckboxLabels';
  }, [step]);

  const rawItems = useMemo(() => {
    const items = getValueByPath<unknown[]>(step, ITEMS_PATH);
    return Array.isArray(items) ? items : [];
  }, [step]);

  const editableItems = useMemo(
    () => transformToEditableItems(rawItems, locale),
    [rawItems, locale]
  );

  // Auto-select first item if none selected
  const hasSetDefaultRef = useRef(false);
  useEffect(() => {
    if (rawItems.length === 0) {
      hasSetDefaultRef.current = false;
      return;
    }
    const hasDefault = rawItems.some((item) => (item as V2ProductOffer).isSelected === true);
    if (!hasDefault && !hasSetDefaultRef.current) {
      hasSetDefaultRef.current = true;
      const newItems = [...rawItems];
      (newItems[0] as Record<string, unknown>).isSelected = true;
      onChange(ITEMS_PATH, newItems);
    } else if (hasDefault) {
      hasSetDefaultRef.current = false;
    }
  }, [rawItems, onChange]);

  const handleCellTypeChange = useCallback(
    (newCellType: SubscriptionCellType) => onChange(V2_SUBSCRIPTION_CELL_TYPE_PATH, newCellType),
    [onChange]
  );

  const handleItemTypeChange = useCallback(
    (newItemType: SubscriptionItemType) => onChange(V2_SUBSCRIPTION_ITEM_TYPE_PATH, newItemType),
    [onChange]
  );

  const handleStylesClick = useCallback(() => {
    setStylesExpanded((prev) => !prev);
  }, []);

  /**
   * Handle item field change — maps flat SubscriptionItemData fields to V2 ProductOffer paths.
   * SubscriptionItemEditor emits: 'productId', 'leftLabelTop', 'leftLabelBottom', etc.
   * We map: 'leftLabelTop' → 'topStartLabel', writing to translations.{locale}
   */
  const handleItemChange = useCallback(
    (index: number, field: keyof SubscriptionItemData, value: unknown) => {
      const newItems = [...rawItems];
      const item = { ...(newItems[index] as V2ProductOffer) };

      if (field === 'productId') {
        item.productId = value as string;
      } else if (field === 'isDefault') {
        // V2: isSelected (was isDefault)
        if (value === true) {
          newItems.forEach((otherItem, i) => {
            if (i !== index) {
              (otherItem as Record<string, unknown>).isSelected = false;
            }
          });
        }
        item.isSelected = value as boolean;
      } else if (field === 'badge') {
        // Badge is a flat ProductBadge: { translations, kind, appearance }
        const badgeText = value as string;
        if (badgeText) {
          const existingBadge = item.badge || { translations: {}, kind: 'static', appearance: {} };
          item.badge = {
            ...existingBadge,
            translations: { ...existingBadge.translations, [locale]: badgeText },
          };
        } else {
          item.badge = null;
        }
      } else if (field !== 'id') {
        // L10n label fields: leftLabelTop → topStartLabel.translations.{locale}
        const v2FieldName = V2_FIELD_MAP[field as string] || (field as string);
        const existingLabel = (item as Record<string, unknown>)[v2FieldName] as Record<string, unknown> | undefined;

        const newTranslations = {
          ...((existingLabel?.translations as Record<string, string>) || {}),
          [locale]: value as string,
        };

        (item as Record<string, unknown>)[v2FieldName] = {
          ...(existingLabel || {
            translations: {},
            kind: 'static',
            parameters: { labels: {}, links: {} },
            appearance: {},
            layout: { insets: {} },
          }),
          translations: newTranslations,
        };
      }

      newItems[index] = item;
      onChange(ITEMS_PATH, newItems);
    },
    [rawItems, locale, onChange]
  );

  const handleReorder = useCallback(
    (reorderedItems: (SubscriptionItemData & { id: string; originalIndex: number })[]) => {
      const newRawItems = reorderedItems.map((item) => rawItems[item.originalIndex]);
      onChange(ITEMS_PATH, newRawItems);
    },
    [rawItems, onChange]
  );

  const handleAddItem = useCallback(() => {
    let newItem: Record<string, unknown>;

    if (rawItems.length > 0) {
      // items[0] is the section-wide template — all "Style settings" (label appearance,
      // badge appearance, checkbox styles, layout insets) are stored on its sub-objects.
      // Clone the whole shape, then reset only per-item content.
      newItem = structuredClone(rawItems[0]) as Record<string, unknown>;

      newItem.productId = '';
      newItem.isSelected = false;
      newItem.weight = 0;

      // Clear label texts, keep appearance + layout from the template
      for (const key of ['topStartLabel', 'bottomStartLabel', 'topEndLabel', 'bottomEndLabel'] as const) {
        const label = newItem[key] as Record<string, unknown> | undefined;
        if (label) {
          label.translations = { [locale]: '' };
        }
      }

      // Badge is per-item content (each plan opts-in). Badge appearance lives on items[0]
      // and is what the runtime renderer uses, so null here is safe.
      newItem.badge = null;
    } else {
      // Empty array — nothing to clone, fall back to factory defaults
      newItem = createDefaultV2ProductOffer(locale);
    }

    const hasDefault = rawItems.some((item) => (item as V2ProductOffer).isSelected === true);
    if (rawItems.length === 0 || !hasDefault) {
      newItem.isSelected = true;
    }
    onChange(ITEMS_PATH, [...rawItems, newItem]);
  }, [rawItems, locale, onChange]);

  const handleDeleteItem = useCallback(
    (index: number) => {
      const newItems = rawItems.filter((_, i) => i !== index);
      const hasDefault = newItems.some((item) => (item as V2ProductOffer).isSelected === true);
      if (!hasDefault && newItems.length > 0) {
        (newItems[0] as Record<string, unknown>).isSelected = true;
      }
      onChange(ITEMS_PATH, newItems);
    },
    [rawItems, onChange]
  );

  return (
    <EditorGuideProvider>
      <SAccordion
        title={sectionTitle ?? 'Subscription Plans'}
        collapsible
        defaultExpanded={defaultExpanded}
        data-testid={getSectionTestId('paywall-subscriptions')}
        sx={{ mb: SECTION_MARGIN_BOTTOM }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SubscriptionTypeSelector
            viewKind={cellType}
            onViewKindChange={(value) => handleCellTypeChange(value as SubscriptionCellType)}
            itemType={itemType}
            onItemTypeChange={(value) => handleItemTypeChange(value as SubscriptionItemType)}
            viewKindOptions={V2_SUBSCRIPTION_CELL_TYPE_OPTIONS}
            itemTypeOptions={V2_SUBSCRIPTION_ITEM_TYPE_OPTIONS}
            onStylesClick={handleStylesClick}
            stylesExpanded={stylesExpanded}
            disabled={disabled}
          />

          <Collapse in={stylesExpanded} timeout="auto" unmountOnExit>
            <SubscriptionStyleSections
              sections={V2_SUBSCRIPTION_STYLE_SECTIONS}
              currentCellType={cellType}
              currentItemType={itemType}
              struct={step as unknown as Record<string, unknown>}
              onChange={onChange}
              disabled={disabled}
              itemsPath={ITEMS_PATH}
              useV2Insets
            />
          </Collapse>

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            disabled={disabled}
            data-testid="add-subscription-item"
            sx={{ alignSelf: 'flex-end' }}
          >
            Add Plan
          </Button>

          {editableItems.length > 0 ? (
            <SortableList
              items={editableItems}
              onReorder={handleReorder}
              gap="sm"
              disabled={disabled}
            >
              {(item, index) => (
                <SortableList.Item
                  key={item.id}
                  showDragHandle
                  dragHandlePosition="start"
                  disabled={disabled}
                >
                  <SubscriptionItemEditor
                    index={index}
                    item={item}
                    stepId={step.id}
                    onChange={(field, value) => handleItemChange(item.originalIndex, field, value)}
                    onDelete={() => handleDeleteItem(item.originalIndex)}
                    canDelete={editableItems.length > 1}
                    disabled={disabled}
                    allProducts={paywallProducts}
                    otherProductIds={editableItems
                      .filter((_, otherIdx) => otherIdx !== index)
                      .map((other) => other.productId)
                      .filter((pid) => pid !== '')}
                    {...paddleConnection}
                    sandboxConnected={sandboxConnected}
                    sandboxNotConnectedReason={sandboxNotConnectedReason}
                    liveConnected={liveConnected}
                    liveNotConnectedReason={liveNotConnectedReason}
                    productsLoading={productsLoading}
                    stripeConnectionLoading={stripeLoading}
                    productsError={productsError}
                    onRetryProducts={refreshPaywallProducts}
                    onCreateProduct={createPaywallProduct}
                    onSaveScreen={onSaveScreen}
                    hasUnsavedChanges={hasUnsavedChanges}
                    pickerPersistenceKey={`${pickerPersistenceScope ?? `${step.id}:paywall-products`}:${item.id}`}
                    locale={locale}
                    projectId={projectId ?? ''}
                  />
                </SortableList.Item>
              )}
            </SortableList>
          ) : (
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                color: 'text.secondary',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              No subscription plans. Click "Add Plan" to create one.
            </Box>
          )}
        </Box>
      </SAccordion>
    </EditorGuideProvider>
  );
};

export default V2PaywallSubscriptionsSection;
